/**
 * VipBenefitsPanel — sits on /my-matches/:sessionId for users who unlocked the bundle.
 * Shows VIP-only benefits: daily-refresh exact matches + ebook access (Buying Guide,
 * Travel Guide). Hidden for users who haven't purchased.
 */

import React from 'react';
import { BookOpen, RefreshCw, Calendar, Sparkles, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VipBenefitsPanel = ({ unlockedTools = [], sessionId, isDark, onRefreshMatches, refreshing }) => {
  const isVip = (unlockedTools || []).length > 0;
  if (!isVip) return null;

  const openEbook = (slug) => {
    // Backend accepts session_id as a token for VIP access
    window.open(`${API}/ebook/${slug}?token=${encodeURIComponent(sessionId || 'dealer')}`, '_blank');
  };

  return (
    <div
      className={`rounded-2xl p-5 sm:p-6 shadow-sm relative overflow-hidden ${
        isDark
          ? 'bg-gradient-to-br from-amber-900/30 via-gray-900 to-gray-900 border border-amber-500/30'
          : 'bg-gradient-to-br from-amber-50 via-white to-white border border-amber-300'
      }`}
      data-testid="vip-benefits-panel"
    >
      {/* Sparkle accent */}
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#F2A900]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="flex items-center gap-2 mb-4 relative">
        <Sparkles className="w-5 h-5 text-[#F2A900]" />
        <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          VIP Tools
        </h3>
        <span className={`ml-auto text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F2A900] text-black`}>
          Unlocked
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 relative">
        {/* Daily exact matches */}
        <div
          className={`p-4 rounded-xl border ${isDark ? 'border-gray-800 bg-gray-900/40' : 'border-amber-200 bg-white'}`}
          data-testid="vip-daily-matches"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
              <Calendar className="w-4 h-4" />
            </div>
            <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Daily Exact Matches</p>
          </div>
          <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Your matches refresh every 24 hours with the latest inventory. Pull the freshest set anytime.
          </p>
          <button
            onClick={onRefreshMatches}
            disabled={refreshing}
            className={`w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${
              isDark ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-amber-500 hover:bg-amber-400 text-black'
            } disabled:opacity-60`}
            data-testid="vip-refresh-matches-btn"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh matches'}
          </button>
        </div>

        {/* RV Buying Guide */}
        <button
          type="button"
          onClick={() => openEbook('rv-buying-guide')}
          className={`p-4 rounded-xl border text-left transition-all ${isDark ? 'border-gray-800 bg-gray-900/40 hover:border-amber-500/60' : 'border-amber-200 bg-white hover:border-amber-400 hover:shadow-md'}`}
          data-testid="vip-ebook-buying-guide"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
              <BookOpen className="w-4 h-4" />
            </div>
            <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>RV Buying Guide</p>
          </div>
          <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            The complete shopper's playbook — pricing tactics, walk-around inspection, financing pitfalls.
          </p>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
            Open ebook <ChevronRight className="w-3 h-3" />
          </span>
        </button>

        {/* RV Travel Guide */}
        <button
          type="button"
          onClick={() => openEbook('rv-travel-guide')}
          className={`p-4 rounded-xl border text-left transition-all ${isDark ? 'border-gray-800 bg-gray-900/40 hover:border-amber-500/60' : 'border-amber-200 bg-white hover:border-amber-400 hover:shadow-md'}`}
          data-testid="vip-ebook-travel-guide"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
              <BookOpen className="w-4 h-4" />
            </div>
            <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>RV Travel Guide</p>
          </div>
          <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Top campgrounds, road-trip routes, off-grid spots, and pro tips for first-time owners.
          </p>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
            Open ebook <ChevronRight className="w-3 h-3" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default VipBenefitsPanel;
